import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Table,
  Box,
  Header,
  SpaceBetween,
  TextFilter,
  Pagination,
  Link,
  Tabs,
  Container,
  FormField,
  Input,
  Button,
  Badge,
  CollectionPreferences,
} from '@amzn/awsui-components-console';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useGuidanceCatalog } from '../../hooks/useGuidanceCatalog';
import { useGuidanceSearch } from '../../hooks/useGuidanceSearch';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatDateTime } from '../../utils/formatFieldName';
import { ErrorState } from '../../components/ErrorState';
import { RetrieverType } from '@amzn/helianthus-arbiter-client';
import type { GuidanceCatalogEntry, GuidanceSearchResult } from '@amzn/helianthus-arbiter-client';

/** Extract the SAX axiom prefix from a guidance path (e.g., "SAX-01" from "SAX-01/outcome_1/Topic"). */
function extractAxiom(path: string): string {
  const match = path.match(/^(SAX-\d+)/);
  return match ? match[1] : '--';
}

/** Extract the topic name from a guidance path (last segment). */
function extractTopic(path: string): string {
  const segments = path.split('/');
  return segments[segments.length - 1]?.replace(/_/g, ' ') ?? path;
}

function CatalogTab() {
  const navigate = useNavigate();
  const { entries, loading, error, refetch } = useGuidanceCatalog();

  const {
    pageSize,
    columnDisplay,
    getColumnWidth,
    handleColumnWidthsChange,
    collectionPreferencesProps,
  } = useTablePreferences({
    storageKey: 'guidance-catalog-table',
    defaults: {
      pageSize: 30,
      visibleContent: ['path', 'axiom', 'topic', 'lastModified'],
    },
    pageSizeOptions: [
      { value: 20, label: '20 entries' },
      { value: 30, label: '30 entries' },
      { value: 50, label: '50 entries' },
      { value: 100, label: '100 entries' },
    ],
    visibleContentOptions: [
      {
        label: 'Columns',
        options: [
          { id: 'path', label: 'Path', editable: false },
          { id: 'axiom', label: 'Axiom' },
          { id: 'topic', label: 'Topic' },
          { id: 'lastModified', label: 'Last modified' },
        ],
      },
    ],
  });

  const columnDefinitions = useMemo(() => [
    {
      id: 'path',
      header: 'Path',
      cell: (item: GuidanceCatalogEntry) => {
        const path = item.path ?? '';
        if (!path) return '--';
        return (
          <Link
            href={`/guidance/${path}`}
            onFollow={(e) => {
              e.preventDefault();
              navigate(`/guidance/${path}`);
            }}
          >
            {path}
          </Link>
        );
      },
      sortingField: 'path',
      width: getColumnWidth('path', 350),
      minWidth: 200,
    },
    {
      id: 'axiom',
      header: 'Axiom',
      cell: (item: GuidanceCatalogEntry) => (
        <Badge color="blue">{extractAxiom(item.path ?? '')}</Badge>
      ),
      sortingComparator: (a: GuidanceCatalogEntry, b: GuidanceCatalogEntry) =>
        extractAxiom(a.path ?? '').localeCompare(extractAxiom(b.path ?? '')),
      width: getColumnWidth('axiom', 120),
      minWidth: 80,
    },
    {
      id: 'topic',
      header: 'Topic',
      cell: (item: GuidanceCatalogEntry) => extractTopic(item.path ?? ''),
      sortingComparator: (a: GuidanceCatalogEntry, b: GuidanceCatalogEntry) =>
        extractTopic(a.path ?? '').localeCompare(extractTopic(b.path ?? '')),
      width: getColumnWidth('topic', 250),
      minWidth: 150,
    },
    {
      id: 'lastModified',
      header: 'Last modified',
      cell: (item: GuidanceCatalogEntry) =>
        formatDateTime(item.lastModified),
      sortingComparator: (a: GuidanceCatalogEntry, b: GuidanceCatalogEntry) => {
        const aTime = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        const bTime = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        return aTime - bTime;
      },
      width: getColumnWidth('lastModified', 200),
      minWidth: 150,
    },
  ], [navigate, getColumnWidth]);

  const { items, filteredItemsCount = 0, collectionProps, filterProps, paginationProps } = useCollection(
    entries,
    {
      filtering: {
        filteringFunction: (item, text) => {
          const search = text.toLowerCase();
          return (
            (item.path?.toLowerCase().includes(search) ?? false)
          );
        },
        empty: (
          <Box textAlign="center" color="inherit">
            <b>No guidance entries</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              The guidance catalog is empty.
            </Box>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>No matches</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No guidance entries match the filter criteria.
            </Box>
          </Box>
        ),
      },
      pagination: { pageSize },
      sorting: {
        defaultState: {
          sortingColumn: columnDefinitions[0],
          isDescending: false,
        },
      },
    },
  );

  if (error) {
    return (
      <ErrorState
        variant="api-error"
        title="Error loading guidance catalog"
        message={error}
        onRetry={refetch}
        backLabel="Go to Dashboard"
        backHref="/"
      />
    );
  }

  return (
    <Table
      {...collectionProps}
      loading={loading}
      loadingText="Loading guidance catalog..."
      resizableColumns
      columnDisplay={columnDisplay}
      onColumnWidthsChange={(event) =>
        handleColumnWidthsChange(
          columnDisplay.filter((c) => c.visible).map((c) => c.id),
          event,
        )
      }
      preferences={
        <CollectionPreferences
          {...collectionPreferencesProps}
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
        />
      }
      columnDefinitions={columnDefinitions}
      items={items}
      header={
        <Header
          variant="h2"
          counter={`(${filteredItemsCount})`}
          description="Browse all available security guidance documents"
        >
          Guidance Catalog
        </Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Filter guidance by path"
          countText={`${filteredItemsCount} match${filteredItemsCount === 1 ? '' : 'es'}`}
        />
      }
      pagination={<Pagination {...paginationProps} />}
    />
  );
}

function SearchTab() {
  const navigate = useNavigate();
  const { results, retrieverUsed, loading, error, search } = useGuidanceSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      setHasSearched(true);
      search(searchQuery.trim(), 20, RetrieverType.GRAPH_RAG);
    }
  }, [searchQuery, search]);

  return (
    <SpaceBetween size="l">
      <Container>
        <SpaceBetween size="m">
          <FormField
            label="Search query"
            description="Enter a natural language query about security guidance, best practices, or compliance requirements"
          >
            <div
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            >
              <Input
                value={searchQuery}
                onChange={({ detail }) => setSearchQuery(detail.value)}
                placeholder="e.g., How do I secure S3 buckets?"
                type="search"
              />
            </div>
          </FormField>
          <Button
            variant="primary"
            onClick={handleSearch}
            loading={loading}
            disabled={!searchQuery.trim()}
          >
            Search Guidance
          </Button>
        </SpaceBetween>
      </Container>

      {error && (
        <ErrorState
          variant="api-error"
          title="Search failed"
          message={error}
          onRetry={handleSearch}
        />
      )}

      {(hasSearched || loading) && (
        <Table
          loading={loading}
          loadingText="Searching guidance..."
          columnDefinitions={[
            {
              id: 'path',
              header: 'Path',
              cell: (item: GuidanceSearchResult) => {
                const path = item.path ?? '';
                if (!path) return '--';
                return (
                  <Link
                    href={`/guidance/${path}`}
                    onFollow={(e) => {
                      e.preventDefault();
                      navigate(`/guidance/${path}`);
                    }}
                  >
                    {path}
                  </Link>
                );
              },
              width: 400,
              minWidth: 200,
            },
            {
              id: 'axiom',
              header: 'Axiom',
              cell: (item: GuidanceSearchResult) => (
                <Badge color="blue">{extractAxiom(item.path ?? '')}</Badge>
              ),
              width: 120,
              minWidth: 80,
            },
            {
              id: 'topic',
              header: 'Topic',
              cell: (item: GuidanceSearchResult) => extractTopic(item.path ?? ''),
              width: 250,
              minWidth: 150,
            },
            {
              id: 'score',
              header: 'Relevance',
              cell: (item: GuidanceSearchResult) =>
                item.score != null ? `${(item.score * 100).toFixed(1)}%` : '--',
              width: 120,
              minWidth: 80,
            },
          ]}
          items={results}
          header={
            <Header
              variant="h2"
              counter={`(${results.length})`}
              description={retrieverUsed ? `Retriever: ${retrieverUsed}` : undefined}
            >
              Search Results
            </Header>
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>No results</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No guidance matched your search query. Try different keywords.
              </Box>
            </Box>
          }
        />
      )}

      {!loading && !hasSearched && !error && (
        <Box textAlign="center" color="text-body-secondary" padding={{ top: 'xl' }}>
          Enter a search query to find relevant security guidance.
        </Box>
      )}
    </SpaceBetween>
  );
}

export function GuidanceListPage() {
  useDocumentTitle('Guidance');

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Search and browse security guidance from the knowledge base">
        Guidance
      </Header>
      <Tabs
        tabs={[
          {
            id: 'catalog',
            label: 'Catalog',
            content: <CatalogTab />,
          },
          {
            id: 'search',
            label: 'Search',
            content: <SearchTab />,
          },
        ]}
      />
    </SpaceBetween>
  );
}
